<template>
    <DsPopover
        hide-arrow
        is-open
        class="inline-add-popover-menu"
        :style="popoverStyle"
    >
        <template #default>
            <div class="inline-add-popover-menu-content">
                <DsInputField
                    ref="searchInput"
                    v-model="searchText"
                    data-qa="search-input"
                    :placeholder="$t('funnel.campaign.search.placeholder')"
                    class="element-search"
                >
                    <template #leading>
                        <DsIcon name="search" />
                    </template>
                    <template #trailing>
                        <span
                            v-show="searchText"
                            class="clear-icon"
                            data-qa="clear-icon"
                            @click="clearSearch"
                        >
                            <DsIcon name="x-circle-fill" />
                        </span>
                    </template>
                </DsInputField>
                <div class="element-list">
                    <div
                        v-if="displayWhens"
                        class="list-group"
                    >
                        <span
                            class="headline-4"
                            data-qa="when-header"
                        >
                            {{ $t('funnel.inlineBetweenMenu.when.trigger') }}
                        </span>
                        <DsListItem
                            v-for="element in whenElements"
                            :key="element.id"
                            :title="element.displayLabel"
                            :data-qa="element.name"
                            class="element-list-item"
                            @click="onElementClick(element)"
                        >
                            <template #leading>
                                <img class="element-icon" :src="element.iconUrl" :alt="element.name" />
                            </template>
                        </DsListItem>
                    </div>
                    <div
                        v-if="thenElements.length > 0"
                        class="list-group"
                    >
                        <span
                            class="headline-4"
                            data-qa="then-header"
                        >
                            {{ $t('funnel.inlineBetweenMenu.then.trigger') }}
                        </span>
                        <DsListItem
                            v-for="element in thenElements"
                            :key="element.id"
                            :title="element.displayLabel"
                            :data-qa="element.name"
                            class="element-list-item"
                            @click="onElementClick(element)"
                        >
                            <template #leading>
                                <img class="element-icon" :src="element.iconUrl" />
                            </template>
                        </DsListItem>
                    </div>
                </div>
            </div>
        </template>
    </DsPopover>
</template>

<script>
import { getIconPathByCellType } from '../utils/cell/automation-display-utils';
import {
    CELL_TYPE,
    WHEN_CELL_OPTIONS,
    THEN_CELL_OPTIONS,
    MAIN_CANVAS_ID,
    PROCESS_EMAIL_CONFIRM,
    SEQUENCE_START,
    VALID_INCOMING_FLOW,
    INTEGRATION_TRIGGER_TYPE,
    INTEGRATION_ACTION_TYPE,
} from '../utils/editor.constants';
import {
    isValid,
} from '../utils/cell/automation-cell-utils';
import {
    findOutgoingCells,
    isValidTimerNextFlow,
} from '../utils/cell/automation-edge-utils';
import {
    displayLabel,
} from '../utils/add-elements.utils';

const OFFSET_BASE_X = 190;
const OFFSET_BASE_Y = 35;
const OFFSET_MULTIPLIER_X = 30;
const OFFSET_MULTIPLIER_Y = 5;
const BIG_CARD_THRESHOLD = 1.5;
const BIG_CARD_OFFSET_X = 216;
const BIG_CARD_OFFSET_X_SEQ = 160;
const BIG_CARD_OFFSET_X_START = 60;

export default {
    props: {
        selectedSequence: {
            type: String,
            required: true,
        },
        inlineBetweenAddData: {
            type: Object,
            required: true,
        },
        enabledCellTypes: {
            type: Array,
            required: true,
        },
        enabledIntegrations: {
            type: Array,
            default: () => [],
        },
        cells: {
            type: Array,
            default: () => ([]),
        },
        isSidebarCollapsed: {
            type: Boolean,
            required: true,
        },
        isSidebarSequenceCollapsed: {
            type: Boolean,
            required: true,
        },
    },

    emits: ['add-cell-between'],

    data() {
        return {
            searchText: '',
        };
    },

    computed: {
        bigCardOffset() {
            if (this.isInSequence) {
                if (this.sourceElement.type === SEQUENCE_START) {
                    return BIG_CARD_OFFSET_X_START;
                }

                return BIG_CARD_OFFSET_X_SEQ;
            }

            return BIG_CARD_OFFSET_X;
        },

        isOutgoingCellAWhen() {
            const outgoingCells = findOutgoingCells(this.sourceElement.id, this.cells);

            if (outgoingCells.length === 0) {
                return false;
            }

            return outgoingCells.some((cell) => this.isAWhen(cell));
        },

        displayWhens() {
            return this.isAThen && !this.isInSequence && this.whenElements.length > 0 && !this.isOutgoingCellAWhen;
        },

        isAThen() {
            return this.type === CELL_TYPE.THEN;
        },

        isInSequence() {
            return this.selectedSequence !== MAIN_CANVAS_ID;
        },

        type() {
            return this.inlineBetweenAddData.type;
        },

        sourceElement() {
            return this.inlineBetweenAddData.sourceElement;
        },

        isBigCardEnabled() {
            return this.inlineBetweenAddData.internalZoomLevel >= BIG_CARD_THRESHOLD;
        },

        popoverStyle() {
            const { event, internalZoomLevel, stagePan, sourceElement } = this.inlineBetweenAddData;
            const container = event?.evt?.srcElement?.getBoundingClientRect();
            const { initialOffsetX, initialOffsetY } = this.calculateOffset(internalZoomLevel);
            const bigCardOffsetX = this.isBigCardEnabled ? (this.bigCardOffset) : 0;
            const zoomDelta = internalZoomLevel - 1;
            let sidebarOffsetX = 0;

            if (this.isInSequence) {
                sidebarOffsetX = this.isSidebarSequenceCollapsed
                    ? zoomDelta * 200
                    : zoomDelta * -200;
            } else if (this.isSidebarCollapsed) {
                sidebarOffsetX = zoomDelta * 350;
            }

            const x = ((sourceElement.mxGeometry.x + container.left) * internalZoomLevel + stagePan.x) + (initialOffsetX / internalZoomLevel) + bigCardOffsetX + sidebarOffsetX;
            const y = ((sourceElement.mxGeometry.y + container.top) * internalZoomLevel + stagePan.y) - (initialOffsetY / internalZoomLevel) + (this.isBigCardEnabled ? 20 : 0);

            return { zIndex: 50000, transform: `translate(${x}px, ${y}px)` };
        },

        whenElements() {
            return this.getCellElements(CELL_TYPE.WHEN);
        },

        thenElements() {
            return this.getCellElements(CELL_TYPE.THEN);
        },
    },

    mounted() {
        this.$nextTick(() => this.$refs.searchInput?.$el.querySelector('input')?.focus());
    },

    methods: {
        getDecimalPartInternalZoomLevel(internalZoomLevel) {
            if (internalZoomLevel >= 1) {
                return Math.round((internalZoomLevel % 1) * 10);
            }

            return 0;
        },

        calculateOffset(internalZoomLevel, baseX = OFFSET_BASE_X, baseY = OFFSET_BASE_Y, multiplierX = OFFSET_MULTIPLIER_X, multiplierY = OFFSET_MULTIPLIER_Y) {
            const initialOffsetX = baseX - (multiplierX * this.getDecimalPartInternalZoomLevel(internalZoomLevel));
            const initialOffsetY = baseY + (multiplierY * this.getDecimalPartInternalZoomLevel(internalZoomLevel));

            return { initialOffsetX, initialOffsetY };
        },

        validCells(cellType) {
            if (cellType in VALID_INCOMING_FLOW) {
                if (this.sourceElement.children.length === 0) return false;
                const validChildren = [];

                this.sourceElement.children.forEach((child) => {
                    if (isValid(child.type, cellType)) {
                        validChildren.push(child);
                    }
                });

                return validChildren.length > 0;
            }

            return true;
        },

        isAWhen(element) {
            return WHEN_CELL_OPTIONS.some((option) => option.name === element.type);
        },

        clearSearch() {
            this.searchText = '';
        },

        getCellElements(type) {
            const isWhen = type === CELL_TYPE.WHEN;
            const options = (isWhen && !this.isInSequence) ? WHEN_CELL_OPTIONS : THEN_CELL_OPTIONS;
            const searchWords = this.searchText.toLowerCase().trim().split(' ');

            const enabledCells = options.filter((cell) => (this.enabledCellTypes.includes(cell.name)))
                .map((cell) => ({
                    ...cell,
                    displayLabel: this.$t(displayLabel(type, cell.name, this.isInSequence)),
                    isIntegration: false,
                }));

            const integrationCells = this.enabledIntegrations.filter((integration) => {
                return integration.sidebarType.toUpperCase() === type.toUpperCase();
            }).map((integration) => {
                return {
                    ...integration,
                    displayLabel: integration.label,
                    isIntegration: true,
                };
            });

            const elements = [...enabledCells, ...integrationCells];

            return elements
                .filter((cell) => {
                    if (this.isInSequence && [PROCESS_EMAIL_CONFIRM, SEQUENCE_START].includes(cell.name)) return false;
                    if (!isValidTimerNextFlow(this.sourceElement.type, cell.name)) return false;
                    if (!this.validCells(cell.name)) return false;
                    if (!this.searchText) return true;
                    const label = this.$t(displayLabel(type, cell.name, this.isInSequence));

                    return searchWords.every((w) => (`${cell.name} ${label}`.toLowerCase().includes(w)));
                })
                .map((cell) => ({
                    ...cell,
                    displayLabel: cell.label || this.$t(displayLabel(type, cell.name, this.isInSequence)),
                    iconUrl: cell.iconUrl || getIconPathByCellType(cell.name, isWhen ? 'green' : 'blue'),
                }));
        },

        onElementClick(element) {
            const newElement = {
                name: element.name,
                displayLabel: element.displayLabel,
                value: {},
            };

            if (element.metaType === INTEGRATION_TRIGGER_TYPE) {
                newElement.value.integrationTriggerId = element.id;
                newElement.displayLabel = element.label;
                newElement.name = INTEGRATION_TRIGGER_TYPE;
            }

            if (element.metaType === INTEGRATION_ACTION_TYPE) {
                newElement.value.integrationActionId = element.id;
                newElement.displayLabel = element.label;
                newElement.name = INTEGRATION_ACTION_TYPE;
            }

            this.$emit('add-cell-between', newElement);
        },
    },
};
</script>


<style scoped lang="scss">
.list-group {
    margin-top: $spacing-250;
}
.headline-4 {
    margin-bottom: $spacing-150;
    color: $color-text-subtle;
    font-size: $font-size-sm;
    font-style: normal;
    font-weight: $font-weight-semibold;
    line-height: $line-height-xs;
}
.element-list {
    padding-left: $spacing-125;
    max-height: px-to-rem(300px);
    overflow-y: auto;
    overflow-x: hidden;
}
.element-icon {
    width: px-to-rem(24px);
    max-height: px-to-rem(24px);
}

.element-list-item {
    --list-item-padding: 0;
    padding-left: $spacing-100;
    cursor: pointer;
    border-radius: $border-radius;

    &:hover {
        background-color: $color-gray-100;
    }
}

.element-search {
    margin: 0;
    border-bottom: 1px solid $color-gray-300;
    --input-border: 0;
}

.clear-icon {
    --icon-size: #{$icon-size-small};
    --icon-color: gray;
    cursor: pointer;
}

.inline-add-popover-menu ::v-deep {
    .list-item-info {
        border: none;
        padding: 0;
    }

    .popover .title {
        background: none;
        border: none;
        padding: $spacing-200 0;
    }
}

</style>
